import { useEffect } from 'react'
import { loadDraft, clearDraft } from '../../utils/spotDraftStorage'
import { trackSpotModeDraftRestored } from '../../utils/analytics'

/**
 * useSpotDraftRestore - Restores a SpotMode draft after the user logs in.
 *
 * Covers two auth paths:
 *  - Regular login: draft is still in React state (pendingAuthSubmit object)
 *  - OAuth redirect: React state was wiped, load from sessionStorage + IndexedDB
 *
 * @atomic @idempotent
 */
export function useSpotDraftRestore({
  user,
  pendingAuthSubmit,
  setPendingAuthSubmit,
  setSpotModeEntity,
  setSpotModeLocation,
  setSpotModeNotes,
  setSpotModeImages,
  setSpotModeEntityType,
  activateSpotMode,
}) {
  useEffect(() => {
    if (!user) return

    const restore = async () => {
      let draft = null
      const restoreMethod = (pendingAuthSubmit && typeof pendingAuthSubmit === 'object') ? 'session' : 'indexeddb'

      if (pendingAuthSubmit && typeof pendingAuthSubmit === 'object') {
        // Regular login: draft is still in React state (has File objects from this session)
        draft = pendingAuthSubmit
        setPendingAuthSubmit(false)
        await clearDraft()
      } else {
        // OAuth redirect: React state was wiped, load from sessionStorage + IndexedDB
        draft = await loadDraft()
        if (!draft) return
        await clearDraft()
      }

      if (draft.entity) setSpotModeEntity(draft.entity)
      if (draft.location) setSpotModeLocation(draft.location)
      if (draft.notes) setSpotModeNotes(draft.notes)
      if (draft.images?.length) setSpotModeImages(draft.images)
      if (draft.entityType) setSpotModeEntityType(draft.entityType)
      // Re-open spot mode so the user sees their form and can submit
      activateSpotMode(null, null, null)

      // Track auth-gate recovery rate
      trackSpotModeDraftRestored(
        restoreMethod,
        !!draft.entity,
        !!draft.location,
        !!(draft.images?.length),
        !!draft.notes
      )
    }

    restore()
  }, [user, pendingAuthSubmit]) // eslint-disable-line react-hooks/exhaustive-deps
}
